'use client';

import { Tables } from '@/types_db';
import { AlertTriangle, Ban, Check, Download, Trash } from 'lucide-react';
import CircleLoading from '@/components/ui/CircleLoading/CircleLoading';
import { progressFromStatus } from '@/utils/helpers';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { EmailModal } from '@/components/ui/EmailModal/EmailModal';
import { useTrainingLive } from '@/components/ui/Tones/Status/TrainingsProvider';
import { usePrevious } from '@/utils/hooks/usePrevious';
import GradientSweep from '@/components/ui/GradientSweep/GradientSweep';
import { DRY_WET_CAPTURE_GUIDE_URL, SWEEP_CAPTURE_GUIDE_URL, TRAIN_ERROR } from '@/utils/constants';
import Button, { LinkButton } from '@/components/ui/Button/Button';
import { InstructModal } from '@/components/ui/InstructModal/InstructModal';
import { ActionModal } from '../../ActionModal/ActionModal';
import LoadingDots from '@/components/ui/LoadingDots';
import Progress from '@/components/ui/Progress/Progress';
import { retryTraining } from '@/utils/training/retry';
import { analytics } from '@/lib/analytics';
type Training = Tables<'trainings'>;

/**
 * "Try Again" action for trainings that failed with an UNKNOWN error
 * (GPU crash / hang / heartbeat timeout). Re-runs the v2 pipeline on
 * the same row and refreshes so live polling picks the run back up.
 */
const RetryTrainingButton = ({
  trainingId,
  onClose,
}: {
  trainingId: number;
  onClose: () => void;
}) => {
  const router = useRouter();

  const onRetry = async () => {
    analytics.capture('training.retried', { trainingId });
    // Close the modal immediately — the retry fires in the background
    // and the row goes back to in-progress on the next refresh.
    onClose();
    const res = await retryTraining(trainingId);
    if (res.ok) {
      // SSR re-run re-enables live polling for the now-active row.
      router.refresh();
    }
  };

  return (
    <Button className="w-full" variant="slim" isPrimary={true} onClick={onRetry}>
      Try Again
    </Button>
  );
};

/**
 * "Retrain Anyway" escape hatch for trainings that failed the
 * consistency (V3 self-ESR) check — `error = TRAIN_ERROR.INCONSISTENT`.
 *
 * Wraps the retry in a confirmation that warns the resulting model may
 * turn out poorly, then re-fires `/api/capture/retry` with
 * `retrainAsDryWet: true`. The route flips the training to a dry-wet pair
 * (dry = the sweep file), which retrains the same audio without the V3
 * self-ESR consistency check that rejected it.
 */
const DangerousRetrainButton = ({ trainingId }: { trainingId: number }) => {
  const router = useRouter();

  const onRetrainAnyway = async (close: () => void) => {
    analytics.capture('training.retrained_as_dry_wet', { trainingId });
    close();
    const res = await retryTraining(trainingId, { retrainAsDryWet: true });
    if (res.ok) {
      router.refresh();
    }
  };

  return (
    <ActionModal
      trigger={
        <Button className="w-full" variant="slim" isSecondary={true}>
          Train Anyway
        </Button>
      }
      title={'Train Anyway?'}
      message={
        <span className={'text-zinc-400 flex flex-col gap-4 text-left'}>
          <span>
            We flagged this recording as inconsistent. You can train it anyway as a Dry/Wet pair,
            which skips our consistency check.
          </span>
          <span className="flex gap-3 items-start p-4 rounded-md border border-[#FF0000] bg-red-950/20">
            <AlertTriangle size={20} className="text-[#FF0000] flex-shrink-0 mt-0.5" />
            <span className="text-sm text-zinc-200">
              Warning: the resulting model could turn out bad. Only continue if you understand the
              risk.
            </span>
          </span>
        </span>
      }
      primaryAction={({ close, loading, setLoading }) => (
        <Button
          className={'w-full text-white'}
          style={{ background: 'red', color: 'white' }}
          variant={'slim'}
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            try {
              await onRetrainAnyway(close);
            } finally {
              setLoading(false);
            }
          }}
        >
          {loading ? <LoadingDots /> : 'Train Dry/Wet'}
        </Button>
      )}
      cancelText={'Cancel'}
    />
  );
};

interface Props {
  training: Partial<Training>;
  modelName: string;
  isOwner: boolean;
}

export const ERROR_MESSAGES = {
  [TRAIN_ERROR.INCONSISTENT]: {
    title: 'Error: Inconsistent Tone',
    message: (
      <span className={'text-zinc-400 flex flex-col gap-4'}>
        The tone is not consistent throughout the uploaded audio. Possible causes:
        <ul className={'pl-4 list-disc flex flex-col gap-0.5'}>
          <li>Excessive noise in the signal chain.</li>
          <li>
            Time-based effects (chorus, delay, reverb) were active, which aren't currently
            supported.
          </li>
          <li>Knob(s) were adjusted during the recording.</li>
          <li>The gear wasn't fully warmed up before recording.</li>
        </ul>
        Please try recording again!
      </span>
    ),
    primaryLink: {
      href: SWEEP_CAPTURE_GUIDE_URL,
      text: 'Upload Instructions',
    },
  },
  [TRAIN_ERROR.MISMATCH]: {
    title: 'Error: Incorrect Sweep Signal',
    message: (
      <span className={'text-zinc-400 flex flex-col gap-4'}>
        <span>
          We couldn't match the uploaded audio with the sweep signal, likely because the provided
          sweep signal wasn't used. Please try again using the correct one.
        </span>
        <span>
          If you did use the correct sweep signal, ensure that you uploaded the right file and check
          for any recording issues.
        </span>
      </span>
    ),
    primaryLink: {
      href: SWEEP_CAPTURE_GUIDE_URL,
      text: 'Upload Instructions',
    },
    secondaryAction: (
      <LinkButton
        href={'/T3K-sweep-v3.wav'}
        variant={'slim'}
        target={'_blank'}
        isSecondary={true}
        download={true}
        tracking={{
          event: 'sweep.file_downloaded',
          properties: {
            from: 'error_modal',
          },
        }}
      >
        <Download size={20} />
        Download Sweep File
      </LinkButton>
    ),
  },
  [TRAIN_ERROR.SHORT]: {
    title: 'Error: Recording Too Short',
    message: (
      <span className={'text-zinc-400 flex flex-col gap-4'}>
        <span>
          Your recording is too short to train a model. Please record a longer take and try again.
        </span>
      </span>
    ),
    primaryLink: {
      href: SWEEP_CAPTURE_GUIDE_URL,
      text: 'Upload Instructions',
    },
  },
  [TRAIN_ERROR.SILENCE]: {
    title: 'Error: Too Much Silence',
    message: (
      <span className={'text-zinc-400 flex flex-col gap-4'}>
        <span>
          Your Dry/Wet Pair has more than 2 seconds of complete silence at the start or end of
          either the input or output recording. This breaks training.
        </span>
        <span>
          Trim the leading and trailing silence on each file.
        </span>
      </span>
    ),
    primaryLink: {
      href: DRY_WET_CAPTURE_GUIDE_URL,
      text: 'Upload Instructions',
    },
  },
  [TRAIN_ERROR.CLIPPED]: {
    title: 'Error: Clipping Detected',
    message: (
      <span className={'text-zinc-400 flex flex-col gap-4'}>
        <span>
          Your recording is clipping &mdash; at least one sample peaks at or above 0 dBFS (full
          scale). Clipped peaks lose information and can&apos;t be modeled accurately.
        </span>
        <span>
          Re-record or normalize the audio a few dB below full scale and re-upload.
        </span>
      </span>
    ),
    primaryLink: {
      href: SWEEP_CAPTURE_GUIDE_URL,
      text: 'Upload Instructions',
    },
  },
  [TRAIN_ERROR.AUDIO]: {
    title: 'Error: Audio Format Issue',
    message: (
      <span className={'text-zinc-400 flex flex-col gap-4'}>
        <span>
          There was an issue processing the audio. Most likely you uploaded 32-bit audio. We
          currently only support 24-bit or 16-bit audio. 32-bit support is coming soon!
        </span>
        <span>
          Please try again with a supported audio format (required: 24-bit, 48kHz, Mono).
        </span>
      </span>
    ),
    primaryLink: {
      href: SWEEP_CAPTURE_GUIDE_URL,
      text: 'Upload Instructions',
    },
  },
  [TRAIN_ERROR.LENGTH]: {
    title: 'Error: Incorrect Length',
    message: (
      <span className={'text-zinc-400 flex flex-col gap-4'}>
        Your upload is not the same length as the sweep file or dry file. Please check the recording in
        your DAW and make sure both files are the same length.
      </span>
    ),
    primaryLink: {
      href: SWEEP_CAPTURE_GUIDE_URL,
      text: 'Upload Instructions',
    },
  },
};

const Status = ({ training: ssrTraining, modelName, isOwner }: Props) => {
  const [suprise, setSuprise] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const router = useRouter();

  const wsTraining = useTrainingLive(ssrTraining.id);
  const training = wsTraining || ssrTraining;

  const isTraining = !training.is_error;
  const progress = progressFromStatus(training.status_text || '');
  const prevIsSuccess = usePrevious(
    !!wsTraining ? !!wsTraining.is_success : !!ssrTraining.is_success
  );

  const onCancel = async (id: number) => {
    setIsCancelling(true);
    const res = await fetch('/api/capture/cancel', {
      method: 'POST',
      body: JSON.stringify({ trainingId: id }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (res.ok) {
      router.refresh();
    }
    setIsCancelling(false);
  };

  useEffect(() => {
    if (!wsTraining) return;
    if (prevIsSuccess === undefined) return;
    const isSuccess = !!wsTraining.is_success;
    if (isSuccess && !prevIsSuccess) setSuprise(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsTraining?.is_success, prevIsSuccess]);

  if (ssrTraining.is_success) return;

  return (
    <>
      {suprise && (
        <GradientSweep
          onFinish={() => {
            router.refresh();
            setSuprise(false);
          }}
        />
      )}
      <div className={'flex flex-col'}>
        <div className={'flex flex-row gap-4 items-center h-6'}>
          <Progress progress={progress} />
          {isOwner && !training.is_success && !training.is_error && (
            <ActionModal
              trigger={
                <span className={`px-2`}>
                  <Ban size={20} />
                </span>
              }
              title={'Cancel Training'}
              message={`Are you sure you want to cancel training "${modelName}"? This action cannot be undone.`}
              primaryAction={
                <Button
                  className={'w-full'}
                  isPrimary={true}
                  onClick={() => training.id && onCancel(training.id)}
                  variant={'slim'}
                >
                  {isCancelling ? <LoadingDots /> : 'Yes, Cancel'}
                </Button>
              }
              cancelText={'Keep Training'}
              disabled={!training.trigger_id || isCancelling}
            />
          )}
          {isOwner && (training.is_success || training.is_error) && (
            <ActionModal
              trigger={
                <span className={'p-2'}>
                  <Trash size={20} />
                </span>
              }
              title={'Delete Model'}
              message={`Are you sure you want to delete "${modelName}"? This action cannot be undone.`}
              primaryAction={
                <Button
                  className={'w-full text-white'}
                  style={{ background: 'red', color: 'white' }}
                  onClick={() => training.id && onCancel(training.id)}
                  variant={'slim'}
                >
                  {isCancelling ? <LoadingDots /> : 'Delete'}
                </Button>
              }
              cancelText={'Cancel'}
            />
          )}
        </div>
        <div className={'flex gap-2 items-center'}>
          <div className={'flex flex-shrink-0'}>
            {training.is_error && <AlertTriangle size={20} color={'#FF0000'} />}
            {isTraining && !training.is_success && (
              <CircleLoading strokeWidth={6} size={20} color={'#d4d4d8'} />
            )}
            {training.is_success && <Check size={20} color={'#00FF00'} />}
          </div>
          <div className={'flex gap-4'}>
            {!training.is_error && (
              <p className="text-zinc-400 inline-flex text-sm">{training.status_text}</p>
            )}
            {training.is_error && !training.error && (
              <EmailModal
                tracking={{
                  event: 'tone.training_error_email_modal.opened',
                }}
                trigger={<span className={'underline text-sm'}>Error: Please Retry</span>}
                title={'Error: Please Retry'}
                message={
                  "The training likely failed due to high demand. We're sorry! Please try again. If the issue persists, contact us and we'll respond ASAP."
                }
                closeButton={false}
                alternateButton={
                  isOwner && training.id
                    ? (close) => <RetryTrainingButton trainingId={training.id!} onClose={close} />
                    : undefined
                }
              />
            )}
            {training.is_error && training.error && (
              <InstructModal
                tracking={{
                  event: 'tone.training_error_instruct_modal.opened',
                }}
                trigger={
                  <span className={'underline text-sm'}>
                    {ERROR_MESSAGES[training.error].title}
                  </span>
                }
                {...ERROR_MESSAGES[training.error]}
                {...(training.error === TRAIN_ERROR.INCONSISTENT && isOwner && training.id
                  ? { secondaryAction: <DangerousRetrainButton trainingId={training.id} /> }
                  : {})}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Status;
